import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ConfigProvider } from 'antd'

import {
  DARK_HEADER_BG,
  DARK_SIDER_BG,
  darkTheme,
  LIGHT_HEADER_BG,
  LIGHT_SIDER_BG,
  lightTheme
} from './index'

/**
 * 亮/暗主题切换 Provider
 *
 * 持久化到 localStorage，支持系统偏好检测。
 * 默认跟随系统 prefers-color-scheme。
 *
 * 同时注入自定义 CSS 变量 --app-bg-sider / --app-bg-header，
 * 供 layout.tsx 引用 sider/header 专用背景色（介于 body 和 card 之间的中间层）。
 */

export type ThemeMode = 'light' | 'dark'

interface ThemeContextValue {
  /** 当前模式 */
  mode: ThemeMode
  /** 切换亮/暗 */
  toggleMode: () => void
  /** 直接设置 */
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'license-admin-theme'

/** 读取系统偏好 */
function getSystemPreference(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** 读取持久化设置（无设置时跟随系统） */
function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
  if (stored === 'light' || stored === 'dark') return stored
  return getSystemPreference()
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode)

  // 持久化 + 切换 html data-theme + 注入自定义 CSS 变量
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode)
    document.documentElement.dataset.theme = mode
    document.documentElement.style.colorScheme = mode
    // sider/header 专用背景色（header 纯白浮于 sider 之上，通过阴影区分）
    const siderBg = mode === 'dark' ? DARK_SIDER_BG : LIGHT_SIDER_BG
    const headerBg = mode === 'dark' ? DARK_HEADER_BG : LIGHT_HEADER_BG
    document.documentElement.style.setProperty('--app-bg-sider', siderBg)
    document.documentElement.style.setProperty('--app-bg-header', headerBg)
  }, [mode])

  // 监听系统偏好变化（仅当用户未手动设置时跟随）
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      // 仅当 localStorage 无设置时跟随系统
      if (!localStorage.getItem(STORAGE_KEY)) {
        setMode(e.matches ? 'dark' : 'light')
      }
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      toggleMode: () => setMode((m) => (m === 'light' ? 'dark' : 'light')),
      setMode
    }),
    [mode]
  )

  const config = mode === 'dark' ? darkTheme : lightTheme

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={config}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  )
}

/** 使用主题上下文 */
export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useThemeMode 必须在 <ThemeProvider> 内部使用')
  }
  return ctx
}
